import React, { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { printerService } from '../services/printerService';
import WebSocketService from '../services/WebSocketService';

// Klucze zapytań dla drukarek
export const printerKeys = {
  all: ['printers'],
  userPrinters: () => [...printerKeys.all, 'userPrinters'],
  discovered: (hubUuid) => [...printerKeys.all, 'discovered', hubUuid],
  printer: (printerUuid) => [...printerKeys.all, 'detail', printerUuid],
};

// Hook do pobierania drukarek użytkownika z WebSocket subskrypcją
export function useUserPrinters(options = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: printerKeys.userPrinters(),
    queryFn: apiService.getUserPrinters,
    staleTime: 5000, // 5 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options,
  });

  // WebSocket subscription for real-time updates
  useEffect(() => {
    const handlePrinterUpdate = (data) => {
      console.log('Received printer update:', data);
      
      // Invalidate and refetch user printers
      queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
      
      // If it's a specific printer update, also invalidate that printer's detail
      if (data.printer_uuid) {
        queryClient.invalidateQueries({ 
          queryKey: printerKeys.printer(data.printer_uuid) 
        });
      }
    };

    const handlePrinterAdded = (data) => {
      console.log('Printer added:', data);
      queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
    };

    const handlePrinterRemoved = (data) => {
      console.log('Printer removed:', data);
      queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
    };

    // Subscribe to printer-related WebSocket events using WebSocketService
    WebSocketService.subscribe('printer_update', handlePrinterUpdate);
    WebSocketService.subscribe('printer_added', handlePrinterAdded);
    WebSocketService.subscribe('printer_removed', handlePrinterRemoved);
    WebSocketService.subscribe('printer_status_changed', handlePrinterUpdate);

    return () => {
      WebSocketService.unsubscribe('printer_update', handlePrinterUpdate);
      WebSocketService.unsubscribe('printer_added', handlePrinterAdded);
      WebSocketService.unsubscribe('printer_removed', handlePrinterRemoved);
      WebSocketService.unsubscribe('printer_status_changed', handlePrinterUpdate);
    };
  }, [queryClient]);

  // Format data for consistent usage
  const formattedData = React.useMemo(() => {
    console.log('Raw query data:', query.data);
    if (!query.data || !query.data.printers) {
      console.log('No data or no printers array');
      return [];
    }

    const formatted = query.data.printers.map((printer, index) => ({
      id: index + 1,
      uuid: printer.uuid,
      name: printer.name,
      serialNumber: printer.serial_number,
      model: printer.model, // Model ID (np. C11)
      modelDisplayName: printer.model_display_name, // Pełna nazwa (np. "Bambu Lab P1P")
      modelInfo: printer.model_info, // Pełne info o modelu
      accessCode: printer.access_code,
      dateAdded: printer.date_added,
      icon_url: printer.icon_url, // Add icon_url mapping
      icon_type: printer.icon_type, // Add icon_type mapping  
      brand: printer.brand, // Add brand mapping
      hub: printer.hub ? {
        id: printer.hub.id,
        uuid: printer.hub.uuid,
        name: printer.hub.name,
        isAuthorized: printer.hub.is_authorized,
        connectionStatus: printer.hub.connection_status,
        avatar_base64: printer.hub.avatar_base64 // Dodaj avatar_base64
      } : null,
      // Status derived from hub connection
      status: printer.hub && printer.hub.is_authorized ? 
        (printer.hub.connection_status?.online ? 'online' : 'offline') : 'disconnected',
      // Simplified status value for StatusBadge component
      statusValue: printer.hub && printer.hub.is_authorized ? 
        (printer.hub.connection_status?.online ? 1 : 0) : 0
    }));

    console.log('Formatted data:', formatted);
    return formatted;
  }, [query.data]);

  return {
    ...query,
    data: formattedData,
    count: formattedData.length
  };
}

// Hook do pobierania szczegółów konkretnej drukarki
export function usePrinterDetail(printerUuid, options = {}) {
  return useQuery({
    queryKey: printerKeys.printer(printerUuid),
    queryFn: () => apiService.getPrinterDetail(printerUuid),
    enabled: !!printerUuid,
    ...options,
  });
}

// Hook do aktualizacji ustawień drukarki
export function useUpdatePrinterSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ printerUuid, data }) => apiService.updatePrinterSettings(printerUuid, data),
    onSuccess: (data, variables) => {
      // Invalidate both user printers and specific printer detail
      queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
      queryClient.invalidateQueries({ queryKey: printerKeys.printer(variables.printerUuid) });
    },
  });
}

// Hook do usuwania drukarki
export function useDeletePrinter() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: apiService.deletePrinter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
    },
  });
}

// Hook do pobierania wykrytych drukarek - zachowujemy dla kompatybilności
export function useDiscoveredPrinters(hubUuid, options = {}) {
  return useQuery({
    queryKey: printerKeys.discovered(hubUuid),
    queryFn: () => printerService.getDiscoveredPrinters(hubUuid),
    enabled: !!hubUuid,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    ...options,
  });
}